import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { CountUp, Reveal } from "@project/user/components/reveal";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import {
  Stethoscope,
  Activity,
  Building2,
  Award,
  PhoneCall,
  Eye,
  Smile,
  Sparkles,
  ChevronRight,
  UserCheck,
  HeartPulse,
  HeartHandshake,
  Hospital,
} from "lucide-react";
import medicalImg from "@project/assets/home-medical.webp";
import {
  hospitalProfileService,
  medicalStaffService,
} from "@project/services/admin";
import { HealthcareDonationModal } from "@project/user/components/healthcare-donation-modal";
import type { HospitalProfile } from "@project/types/hospital-profile";
import type { MedicalStaff } from "@project/types/medical-staff";

function getUnitIcon(name: string) {
  const lowercaseName = name.toLowerCase();
  if (lowercaseName.includes("eye")) {
    return Eye;
  }
  if (lowercaseName.includes("dental") || lowercaseName.includes("dentist")) {
    return Smile;
  }
  if (lowercaseName.includes("physio") || lowercaseName.includes("rehab")) {
    return Activity;
  }
  return Stethoscope; // default
}

// Stats Ticker
const HEALTHCARE_STATS = [
  {
    label: "Monthly Cataract Surgeries",
    value: 125,
    prefix: "",
    suffix: "+",
    icon: Sparkles,
  },
  {
    label: "Daily Eye OPD Patients",
    value: 50,
    prefix: "~",
    suffix: "+",
    icon: Eye,
  },
  {
    label: "Monthly Dental OPD Patients",
    value: 100,
    prefix: "~",
    suffix: "+",
    icon: Smile,
  },
  {
    label: "Daily Physiotherapy IPD Sessions",
    value: 60,
    prefix: "~",
    suffix: "+",
    icon: Activity,
  },
  {
    label: "Free Rural Eye Check-up Camps",
    value: 76,
    prefix: "",
    suffix: "+",
    icon: Award,
  },
];

export default function HealthcareIndexPage() {
  const [profiles, setProfiles] = useState<HospitalProfile[]>([]);
  const [staffList, setStaffList] = useState<MedicalStaff[]>([]);
  const [donationOpen, setDonationOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [profilesRes, staffRes] = await Promise.all([
          hospitalProfileService.listPage({ page: 1, page_size: 100 }),
          medicalStaffService.listPage({ page: 1, page_size: 100 }),
        ]);
        setProfiles((profilesRes.data || []).filter(p => p.is_active));
        setStaffList(staffRes.data || []);
      } catch (err) {
        console.error("Failed to load healthcare page data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const highlightedStaff = useMemo(() => {
    return staffList.find(s => s.is_highlighted && s.is_active);
  }, [staffList]);

  const handleSelectSection = (id: number) => {
    navigate({
      to: "/healthcare/$id" as any,
      params: { id: String(id) } as any,
    });
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        eyebrow="BHAGWAN MAHAVIR HEALTHCARE INITIATIVE"
        title="Bhagwan Mahavir Healthcare Services"
        subtitle="Comprehensive medical care, eye care hospital, dental clinic, and Gujarat's 1st Vruddhashram rehabilitation center — serving senior citizens and the community with dedication."
        image={medicalImg}
      >
        <div className="flex flex-wrap items-center justify-center gap-3 pt-10">
          <Button
            size="lg"
            onClick={() => {
              const el = document.getElementById("sections-selector");
              el?.scrollIntoView({ behavior: "smooth" });
            }}
            className="gap-2 bg-primary text-sm font-semibold text-primary-foreground shadow-sm"
          >
            <Building2 className="h-4 w-4" /> Explore Healthcare Units
          </Button>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => setDonationOpen(true)}
            className=" hover:bg-primary/10 bg-gold backdrop-blur-xs gap-2 text-sm font-semibold text-foreground"
          >
            <HeartHandshake className="h-4 w-4 text-primary" /> Support &amp;
            Donate
          </Button>
          <HealthcareDonationModal open={donationOpen} onOpenChange={setDonationOpen} />
        </div>
      </PageHero>

      <div className="mx-auto max-w-7xl space-y-16 px-4 py-12">
        {/* SECTION SELECTOR (4 MAIN SECTIONS GRID) */}
        <section id="sections-selector" className="scroll-mt-20 space-y-6">
          <div className="mx-auto max-w-2xl space-y-2 text-center">
            <Reveal>
              <div className="mx-auto max-w-3xl text-center">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                  On-Campus Medical Centers
                </p>
                <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  Select a Healthcare Unit
                </h2>
                <p className="mt-3 text-muted-foreground">
                  Click on any section below to view its complete medical
                  services, doctors, equipment, and highlights.
                </p>
              </div>
            </Reveal>
          </div>

          {profiles.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border py-12 text-center">
              <Stethoscope className="mx-auto h-12 w-12 animate-pulse text-muted-foreground" />
              <h3 className="mt-4 font-serif text-lg font-bold text-foreground">
                No Healthcare Units Registered
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Please check back later or set up units in the admin panel.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {profiles.map(sec => {
                const Icon = getUnitIcon(sec.hospital_name);
                const badgeText = sec.highlights?.[0]?.title || "Care Services";
                return (
                  <button
                    key={sec.id}
                    type="button"
                    onClick={() => handleSelectSection(sec.id)}
                    className="border-border/80 hover:shadow-xs group relative flex cursor-pointer flex-col justify-between space-y-4 overflow-hidden rounded-2xl border bg-white p-6 text-left shadow-xl transition-all duration-300 hover:border-amber-500/30 hover:to-amber-500/15"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="shadow-2xs flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border  bg-[#ffbd3d] text-gray-800">
                          <Hospital  className="h-6 w-6" />
                        </div>
                        <Badge
                          variant="secondary"
                          className="border-green-800 bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800 transition-colors group-hover:bg-green-200 group-hover:text-green-900"
                        >
                          {badgeText}
                        </Badge>
                      </div>

                      <div className="space-y-1">
                        <h3 className="font-serif text-base font-extrabold leading-snug text-foreground transition-colors group-hover:text-[#0052b4]">
                          {sec.hospital_name}
                        </h3>
                        <p className="line-clamp-2 text-xs font-medium leading-relaxed text-muted-foreground">
                          {sec.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className="border-border/50 flex items-center justify-between border-t pt-2 text-xs font-bold">
                      <span className="text-primary">
                        Click to view data
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Metrics */}
        <section className="border-border/60 bg-card/40">
          <p className="text-center text-xs font-bold uppercase tracking-[0.22em] text-accent">
            ❋ TRUSTED HEALTHCARE IMPACT ACROSS GUJARAT ❋
          </p>
          <div className="mx-auto grid max-w-7xl gap-4 px-0 py-12 sm:grid-cols-2 lg:grid-cols-5">
            {HEALTHCARE_STATS.map((stat, idx) => (
              <Reveal key={stat.label} delay={idx * 80}>
                <div className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5">
                  <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                    <HeartPulse className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-serif text-3xl font-semibold text-foreground">
                      <CountUp to={stat.value} duration={1600} />
                      {stat.suffix}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* SECTION: CONTACT & HELPLINE STRIP */}

        {highlightedStaff && (
          <section
            id="contact-healthcare"
            className="border-primary/20 shadow-xs mx-auto max-w-5xl rounded-2xl border bg-card p-6 sm:p-7"
          >
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              {/* Medical Incharge */}
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-primary">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0 text-xs sm:text-sm">
                  <span className="block text-[11px] font-bold uppercase text-muted-foreground sm:mr-2 sm:inline">
                    Medical In-charge:
                  </span>
                  <span className="font-serif text-sm font-bold text-foreground sm:text-base">
                    {highlightedStaff
                      ? `${highlightedStaff.name} (${highlightedStaff.designation})`
                      : "Sejal Shah (Chief Coordinator)"}
                  </span>
                </div>
              </div>

              <div className="bg-border/60 hidden h-9 w-px md:block" />

              {/* Helpline */}
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700">
                  <PhoneCall className="h-5 w-5" />
                </div>
                <div className="min-w-0 text-xs sm:text-sm">
                  <span className="block text-[11px] font-bold uppercase text-amber-700 sm:mr-2 sm:inline">
                    24/7 Emergency Helpline:
                  </span>
                  <a
                    href="tel:9978930950"
                    className="text-sm font-black text-amber-700 hover:underline sm:text-base"
                  >
                    9978930950
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </PageShell>
  );
}
